import { isValid } from "date-fns";
import { twMerge } from "tailwind-merge";
import { clsx, type ClassValue } from "clsx";
import { intervalToDuration } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function assert<T>(value: T, message: string): asserts value is NonNullable<T> {
  if (value === null || value === undefined) throw new Error(message);
}

export function createOrCurrentDate(date?: string | number | Date | null) {
  const value = date ? new Date(date) : new Date();
  return isValid(value) ? value : new Date();
}

export function createDateInstance(date?: Date | string | number) {
  return date ? new Date(date) : new Date();
}

export function createDateOrDefault(date: string | number | Date | null | undefined, defaultDate: Date = new Date()) {
  if (!date) return defaultDate;
  else return new Date(date);
}

export function coerceNullToUndefined<T>(value: T): T | undefined {
  return value === null ? undefined : value;
}

export function createURLPath(...path: string[]) {
  return path.join("/");
}

export function extractWebsiteSource(website: string) {
  return website.split(".")[0] || website || "unknown";
}

export function createFormData(data: Record<string, [File | string, string?]>) {
  const form = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (value[0] instanceof File) {
      form.append(key, value[0], value[1]);
    } else {
      form.append(key, value[0]);
    }
  });
  return form;
}

export function slugify(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
}

export function formatPascalCase(input: string): string {
  return input.replace(/([a-z])([A-Z])/g, "$1 $2");
}

export function createQueryString(params: Record<string, string | number | boolean | null | undefined>) {
  return Object.entries(params)
    .filter(([, value]) => value !== null && value !== undefined)
    .map(([key, value]) => `${key}=${value}`)
    .join("&");
}

export function sanitizeFileName(name: string) {
  return name
    .replace(/[^a-zA-Z0-9.-]/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_+|_+$/g, "");
}

export async function downloadFile(blob: Blob | File, name: string) {
  const source = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = source;
  a.download = name;
  document.body.appendChild(a);

  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(source);
}

export function formatDurationMs(ms: number) {
  const duration = intervalToDuration({ start: 0, end: ms });
  const minutes = String(duration.minutes || 0).padStart(2, "0");
  const seconds = String(duration.seconds || 0).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

export function normalizeWebsite(website: string): string {
  if (!website) {
    throw new Error("Website is required. Supported formats are https://example.com or example.com");
  }

  const lowercased = website.toLowerCase().trim();
  const pattern = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)*([a-zA-Z0-9-]+)\.([a-zA-Z0-9.-]+)(\/.*)?$/;

  if (!pattern.test(lowercased)) {
    throw new Error("Website is not valid. Supported formats are https://example.com or example.com");
  }

  if (!lowercased.startsWith("http://") && !lowercased.startsWith("https://")) {
    return `https://${lowercased}`;
  }

  return lowercased;
}

export function pickNearestFontWeight(available: number[], desired: number) {
  if (!available.length) return desired;
  if (available.includes(desired)) return desired;

  const idx = available.findIndex((w) => w >= desired);
  if (idx === -1) return available[available.length - 1];
  if (idx === 0) return available[0];

  const lower = available[idx - 1];
  const upper = available[idx];
  const dLower = Math.abs(desired - lower);
  const dUpper = Math.abs(upper - desired);
  return dLower <= dUpper ? lower : upper;
}
